import type { FetchParams } from '../hooks/useRemoteDataV2';
import { getNestedValue } from './remoteDataHelpers';

export interface PageRange {
  startPage: number;
  endPage: number;
}

export interface ParsedListResponse {
  items: Record<string, unknown>[];
  totalCount: number;
}

export function computePageRange(
  startIndex: number,
  endIndex: number,
  pageSize: number,
): PageRange {
  const startPage = Math.floor(startIndex / pageSize);
  const endPage = Math.floor(Math.max(0, endIndex - 1) / pageSize);
  return { startPage, endPage };
}

export function resolveRowLocation(
  absoluteIndex: number,
  pageSize: number,
): { pageNum: number; indexInPage: number } {
  return {
    pageNum: Math.floor(absoluteIndex / pageSize),
    indexInPage: absoluteIndex % pageSize,
  };
}

export function lookupCachedRow(
  pageCache: ReadonlyMap<number, Record<string, unknown>[]>,
  absoluteIndex: number,
  pageSize: number,
): Record<string, unknown> | null {
  const { pageNum, indexInPage } = resolveRowLocation(absoluteIndex, pageSize);
  const page = pageCache.get(pageNum);
  if (!page) return null;
  return page[indexInPage] ?? null;
}

export function buildRandomAccessQuerySignature(
  fetchParams: Pick<FetchParams, 'searchText' | 'filters' | 'sort'>,
  pageSize: number,
): string {
  return JSON.stringify({
    searchText: fetchParams.searchText,
    filters: fetchParams.filters,
    sort: fetchParams.sort,
    pageSize,
  });
}

export function parseListResponse(
  responseData: unknown,
  responseMapping: { items: string; totalCount: string },
): ParsedListResponse {
  if (Array.isArray(responseData)) {
    return {
      items: responseData as Record<string, unknown>[],
      totalCount: responseData.length,
    };
  }

  const items = getNestedValue(responseData, responseMapping.items);
  const count = getNestedValue(responseData, responseMapping.totalCount);
  const parsedItems = Array.isArray(items) ? (items as Record<string, unknown>[]) : [];

  return {
    items: parsedItems,
    totalCount: typeof count === 'number' ? count : parsedItems.length,
  };
}
